/**
 * Running a share, and knowing whether one is possible.
 *
 * This owns the signalling connection for as long as somebody is signed in, so the button
 * can say how many other sessions are there, and it answers whatever those sessions ask
 * of it. Pressing the button is the other half: this session becomes the hub for one round
 * — collect every manifest, work out the union, pull, then push — which is what makes one
 * press converge all the sessions rather than only this one.
 *
 * ## Sharing from a headset
 *
 * Starting a share needs the button, and the button is not in the VR view, so a share can
 * only be *started* outside VR. Answering one is allowed at any time, including mid-session
 * in a headset, and that asymmetry is deliberate: the case the whole feature exists for is
 * getting models onto a headset, and a headset that refused to listen while being worn
 * would be no use for it. What arrives turns up in the in-VR menu like anything else.
 */

import { logger } from '../log.js';
import type { Health } from '../domain/health.js';
import { plannedBytes, planShare, type Manifest, type PeerId } from '../domain/sync.js';
import type { ModelStore } from '../storage/modelStore.js';
import { Conversation } from './conversation.js';
import { installId } from './installId.js';
import { NotReachableError, PeerLinks, PROBE_CHANNEL } from './peerLinks.js';
import { type PeerView, Signalling } from './signalling.js';
import { StoreLibrary } from './storeLibrary.js';
import { ALIVE, ALIVE_ACK, PeerWatch, readAlive } from './watch.js';
import { wireOf } from './wire.js';

const log = logger('share');

/**
 * How long to wait for a peer to confirm it has stored what it was sent.
 *
 * Only ever reached when something has gone wrong: the confirmation is sent as soon as the
 * receiving side has finished writing, so a healthy peer answers immediately.
 */
const FAREWELL_TIMEOUT_MS = 30_000;

/**
 * How long the answering side waits to be hung up on before hanging up itself.
 *
 * The mirror of the problem [`FAREWELL_TIMEOUT_MS`] solves. Having acknowledged a share,
 * this side closing first would tear down the transport with its own acknowledgement
 * possibly still in flight — leaving the other end to sit out its full timeout. Letting
 * the side that started the share close it is both tidier and one fewer race.
 */
const HANGUP_TIMEOUT_MS = 10_000;

/** Nobody else is signed in to this account right now. */
export class NoOtherSessionsError extends Error {
  constructor() {
    super('No other sessions of this account are open.');
    this.name = 'NoOtherSessionsError';
  }
}

/** Other sessions are there, but no direct connection to any of them could be opened. */
export class NoRouteError extends Error {
  constructor() {
    super('No direct connection to your other sessions could be opened.');
    this.name = 'NoRouteError';
  }
}

export interface ShareResult {
  /** Sessions actually reached. */
  readonly peers: number;
  /** Sessions that were there but could not be connected to. */
  readonly unreachable: number;
  readonly received: number;
  readonly sent: number;
  /** Models this share deleted here, because another session had deleted them. */
  readonly removed: number;
  /**
   * Models this share deleted on other sessions.
   *
   * Counted rather than inferred from the tombstones sent, most of which are only records
   * of deletions the receiving session had never heard of and had nothing to delete. This
   * is the number that actually changed something over there — and without it, sharing a
   * deletion reports "nothing to copy", which reads as if nothing happened at all.
   */
  readonly removedElsewhere: number;
  /** Models whose colour or size this share changed here, decided elsewhere. */
  readonly revised: number;
  /** Models whose colour or size this share changed on other sessions. */
  readonly revisedElsewhere: number;
  /** Models a peer sent that would not parse, and were therefore not stored. */
  readonly refused: number;
}

export interface Progress {
  readonly movedBytes: number;
  readonly totalBytes: number;
}

export interface SharingOptions {
  readonly store: ModelStore;
  readonly apiBaseUrl: string;
  readonly csrfToken: () => string | undefined;
  /** How many other sessions are present; drives what the button says. */
  readonly onPresence: (others: number) => void;
  /**
   * Whether those sessions could actually be shared with; drives the indicator.
   *
   * Separate from the count above because the two answer different questions and can
   * disagree: three sessions can be signed in with not one of them reachable.
   */
  readonly onHealth: (health: Health) => void;
  /** Something changed in the library, from either end of a share. */
  readonly onLibraryChanged: () => void;
  /** A model on display has been deleted elsewhere and must be let go of. */
  readonly onModelsRemoved: (ids: readonly string[]) => void;
  /** A model's colour or size was changed elsewhere and must be redrawn here. */
  readonly onModelsRevised: (ids: readonly string[]) => void;
}

export class Sharing {
  private readonly signalling: Signalling;
  private readonly links: PeerLinks;
  private readonly watch: PeerWatch;
  private readonly install = installId();

  /** Other sessions currently connected, by name. Excludes this browser's own tabs. */
  private peers = new Map<PeerId, PeerView>();

  private account: string | undefined;
  private busy = false;

  constructor(private readonly options: SharingOptions) {
    this.signalling = new Signalling(
      options.apiBaseUrl,
      this.install,
      options.csrfToken,
      {
        onReady: (_self, peers, ice) => {
          this.links.setIceConfig(ice);
          this.peers = new Map(
            peers.filter((peer) => peer.install !== this.install).map((p) => [p.peer, p]),
          );
          this.watch.setConnected(true);
          this.watch.setPeers([...this.peers.keys()]);
          this.announce();
        },
        onJoined: (peer) => {
          // Another tab of this same browser shares this library already; copying to it
          // would be work with nothing to show for it.
          if (peer.install === this.install) return;
          this.peers.set(peer.peer, peer);
          this.watch.add(peer.peer);
          this.announce();
        },
        onLeft: (peer) => {
          this.watch.remove(peer);
          if (this.peers.delete(peer)) this.announce();
        },
        onSignal: (from, payload) => {
          // Liveness is settled here rather than in `PeerLinks`, which is about opening
          // connections and has no business with a message that never opens one.
          const alive = readAlive(payload);
          if (alive !== undefined) {
            this.watch.heard(from);
            // Answered from the message handler, so that a tab whose timers the browser
            // has throttled still replies at once. That is the difference between a
            // session left in the background and one that has genuinely gone to sleep.
            if (alive === ALIVE) this.signalling.send(from, { kind: ALIVE_ACK });
            return;
          }

          this.links.handleSignal(from, payload);
        },
        onDown: () => {
          this.peers.clear();
          this.links.closeAll();
          this.watch.setConnected(false);
          this.announce();
        },
      },
    );

    this.links = new PeerLinks(this.signalling, (peer, channel) => {
      this.answer(peer, channel);
    });

    this.watch = new PeerWatch({
      ping: (peer) => {
        this.signalling.send(peer, { kind: ALIVE });
      },
      probe: async (peer) => this.links.probe(peer),
      onChange: options.onHealth,
      now: () => Date.now(),
    });
  }

  /** Follow the signed-in account: connect when there is one, disconnect when there is not. */
  setAccount(accountId: string | undefined): void {
    if (this.account === accountId) return;
    this.account = accountId;

    if (accountId === undefined) {
      this.signalling.stop();
      this.links.closeAll();
      this.watch.stop();
      this.peers.clear();
      this.announce();
      return;
    }

    this.signalling.start();
    this.watch.start();
  }

  get otherSessions(): number {
    return this.peers.size;
  }

  /** Which account this session is sharing as, if any. */
  get accountId(): string | undefined {
    return this.account;
  }

  /**
   * Copy models both ways with every other session of this account.
   *
   * Rejects with [`NoOtherSessionsError`] if there is nobody to share with, and with
   * [`NoRouteError`] if there is but no connection can be opened — which is the case the
   * interface has to explain rather than merely report.
   */
  async share(onProgress: (progress: Progress) => void): Promise<ShareResult> {
    const owner = this.account;
    if (owner === undefined) throw new NoOtherSessionsError();
    if (this.busy) throw new Error('A share is already running.');

    this.busy = true;
    this.watch.setBusy(true);
    try {
      return await this.run(owner, onProgress);
    } finally {
      this.busy = false;
      this.watch.setBusy(false);
    }
  }

  private async run(
    owner: string,
    onProgress: (progress: Progress) => void,
  ): Promise<ShareResult> {
    // Anything stored before sharing existed has no identity yet, and a model that cannot
    // be identified cannot be reconciled.
    await this.options.store.ensureHashes();

    const present = [...this.peers.keys()];
    if (present.length === 0) throw new NoOtherSessionsError();

    let moved = 0;
    let total = 0;
    const report = (bytes: number): void => {
      moved += bytes;
      onProgress({ movedBytes: moved, totalBytes: total });
    };

    const library = new StoreLibrary(this.options.store, owner, {
      onAdded: () => {
        this.options.onLibraryChanged();
      },
      onRemoved: (ids) => {
        this.options.onModelsRemoved(ids);
        this.options.onLibraryChanged();
      },
      onRevised: (ids) => {
        this.options.onModelsRevised(ids);
        this.options.onLibraryChanged();
      },
    });

    const open: { peer: PeerId; channel: RTCDataChannel; talk: Conversation }[] = [];
    let unreachable = 0;

    for (const peer of present) {
      try {
        const channel = await this.links.open(peer);
        // The best evidence there is about reaching this session, and free: the indicator
        // spends no time probing something a share has just proved either way.
        this.watch.record(peer, true);
        open.push({
          peer,
          channel,
          talk: new Conversation(wireOf(channel), library, report),
        });
      } catch (error) {
        if (!(error instanceof NotReachableError))
          log.warn('A share with a session failed.', error);
        this.watch.record(peer, false);
        unreachable += 1;
      }
    }

    if (open.length === 0) throw new NoRouteError();

    try {
      const manifests = new Map<PeerId, Manifest>();
      for (const link of open) manifests.set(link.peer, await link.talk.askManifest());

      const plan = planShare(await library.manifest(), manifests);
      total = plannedBytes(plan);
      onProgress({ movedBytes: moved, totalBytes: total });

      // Pull everything first, so that what is pushed afterwards includes what this
      // session has only just learned from the other peers.
      for (const link of open) {
        const wanted = plan.pull.get(link.peer);
        if (wanted) await link.talk.pull(wanted);
      }

      await library.forget(plan.local.tombstones);
      await library.revise(plan.local.amend);

      let sent = 0;
      let removedElsewhere = 0;
      let revisedElsewhere = 0;

      for (const link of open) {
        const stones = plan.tombstonesFor.get(link.peer) ?? [];
        link.talk.sendTombstones(stones);

        // Only for models the peer already holds. Anything it is about to be sent carries
        // the merged colour and size in its own header, so nothing is said twice.
        const changes = plan.amendFor.get(link.peer) ?? [];
        link.talk.sendAmendments(changes);
        revisedElsewhere += changes.length;

        // Most tombstones only tell a session about a deletion it had never heard of and
        // has nothing to act on. The ones worth reporting are those the peer still holds
        // the file for, since those are the models this share is about to remove there.
        const held = new Set(manifests.get(link.peer)?.models.map((m) => m.hash));
        removedElsewhere += stones.filter((stone) => held.has(stone.hash)).length;

        const outgoing = plan.push.get(link.peer) ?? [];
        await link.talk.push(outgoing);
        sent += outgoing.length;

        link.talk.finish();
      }

      // Wait to be told everything landed before closing anything. Closing a peer
      // connection tears down its transport at once, so hanging up the moment the last
      // chunk is handed to the network discards whatever the other end had not yet
      // written — which loses the last file of a share, intermittently and silently.
      await Promise.all(
        open.map(async (link) => {
          try {
            await withTimeout(link.talk.untilPeerFinished(), FAREWELL_TIMEOUT_MS);
          } catch (error) {
            log.warn(`Session ${link.peer} did not confirm the transfer.`, error);
          }
        }),
      );

      const counts = library.counts;
      return {
        peers: open.length,
        unreachable,
        received: counts.accepted,
        sent,
        removed: counts.removed,
        removedElsewhere,
        revised: counts.revised,
        revisedElsewhere,
        refused: counts.refused,
      };
    } finally {
      for (const link of open) this.links.release(link.channel);
      this.options.onLibraryChanged();
    }
  }

  /** Play the answering part for a share somebody else started. */
  private answer(peer: PeerId, channel: RTCDataChannel): void {
    // Not a share at all: the other session only wanted to know whether it could open a
    // connection, and opening it was the whole of the answer. Worth recording here too —
    // the route it just proved is the same route this session would use, so its check
    // saves this one from making its own.
    if (channel.label === PROBE_CHANNEL) {
      this.watch.record(peer, true);
      // Let the side that opened it close it, for the reason [`HANGUP_TIMEOUT_MS`] gives:
      // hanging up first tears the transport down under an acknowledgement still in
      // flight, and here that would make a successful probe look like a failed one.
      void untilClosed(channel, HANGUP_TIMEOUT_MS).finally(() => {
        this.links.release(channel);
      });
      return;
    }

    const owner = this.account;
    if (owner === undefined) {
      // Signed out between the offer and the channel opening. Nothing to share, and
      // nothing of anyone else's to accept.
      channel.close();
      return;
    }

    const library = new StoreLibrary(this.options.store, owner, {
      onAdded: () => {
        this.options.onLibraryChanged();
      },
      onRemoved: (ids) => {
        this.options.onModelsRemoved(ids);
        this.options.onLibraryChanged();
      },
      onRevised: (ids) => {
        this.options.onModelsRevised(ids);
        this.options.onLibraryChanged();
      },
    });

    const talk = new Conversation(wireOf(channel), library);

    void talk
      .untilPeerFinished()
      // Then let the session that started the share close it; see the note above.
      .then(async () => untilClosed(channel, HANGUP_TIMEOUT_MS))
      .catch((error: unknown) => {
        log.warn(`A share from session ${peer} ended early.`, error);
      })
      .finally(() => {
        this.links.release(channel);
        this.options.onLibraryChanged();
      });
  }

  private announce(): void {
    this.options.onPresence(this.peers.size);
  }
}

/** Wait for the other end to hang up, or for long enough that it plainly is not going to. */
async function untilClosed(channel: RTCDataChannel, milliseconds: number): Promise<void> {
  if (channel.readyState === 'closed' || channel.readyState === 'closing') return;

  await new Promise<void>((resolve) => {
    const finish = (): void => {
      clearTimeout(timer);
      channel.removeEventListener('close', finish);
      resolve();
    };
    const timer = setTimeout(finish, milliseconds);
    channel.addEventListener('close', finish);
  });
}

/** Give up waiting rather than leaving a share running for as long as a tab is open. */
async function withTimeout<T>(work: Promise<T>, milliseconds: number): Promise<T> {
  let timer: ReturnType<typeof setTimeout> | undefined;
  try {
    return await Promise.race([
      work,
      new Promise<never>((_, reject) => {
        timer = setTimeout(() => {
          reject(new Error('timed out'));
        }, milliseconds);
      }),
    ]);
  } finally {
    if (timer !== undefined) clearTimeout(timer);
  }
}
